<!--
 * @Description: 充电枪白名单配置列表
-->
<template>
  <scroll-layout class="gun-whitelist-list">
    <template slot="header">
      <ykc-search
        ref="YkcSearch"
        :data="searchData"
        :searchForm="searchParams"
        @clean="handleSearchClean"
        @click="handleSearchConfirm"></ykc-search>
    </template>
    <ykc-table
      ref="YkcTable"
      operateFixedType="right"
      :selectable="true"
      :data="tableData"
      title="充电枪白名单列表"
      :columns="tableColumns"
      :operateButtons="tableOperateButtons.filter(item => item.enabled())"
      @selection-change="handleSelectionChange">
      <template #headerButtons>
        <div class="table-header-button">
          <ykc-button v-rbac="'operation:gunWhitelist:add'" type="plain" @click="handleAdd">
            添加
          </ykc-button>
          <ykc-button
            v-rbac="'operation:gunWhitelist:enable'"
            type="plain"
            :disabled="selectedIds.length === 0"
            @click="handleBatchEnable">
            批量开启
          </ykc-button>
          <ykc-button
            v-rbac="'operation:gunWhitelist:disable'"
            type="plain"
            :disabled="selectedIds.length === 0"
            @click="handleBatchDisable">
            批量关闭
          </ykc-button>
        </div>
      </template>

      <ykc-pagination
        align="right"
        slot="pagination"
        ref="YkcPagination"
        :total="pageInfo.total"
        :page-size.sync="pageInfo.size"
        :current-page.sync="pageInfo.current"
        @size-change="handlePaginationSizeChange"
        @current-change="handlePaginationCurrentChange"></ykc-pagination>
    </ykc-table>

    <!-- 添加/编辑抽屉 -->
    <ykc-drawer
      :title="drawerTitle"
      :show.sync="showDrawer"
      :on-close="onDrawerClose"
      :before-cancel="onDrawerCancel">
      <template #footer>
        <div class="drawer-button-wrap">
          <template v-for="(btn, i) in drawerButtons">
            <ykc-button :type="btn.type" v-if="btn.enabled()" :key="i" @click="btn.handler">
              {{ btn.label }}
            </ykc-button>
          </template>
        </div>
      </template>
      <div class="drawer-main-body" v-if="showDrawer">
        <AddOrEdit
          ref="addOrEdit"
          :drawerType="drawerType"
          :editData="currentRow"
          @success="handleDrawerSuccess"></AddOrEdit>
      </div>
    </ykc-drawer>

    <!-- 二次确认弹窗 -->
    <ykc-operation-dialog
      :title="confirmTitle"
      :show.sync="confirmVisible"
      :before-close="onConfirmClose"
      :before-confirm="handleConfirmSubmit"
      :confirm-loading="confirmLoading"
      confirm-btn-txt="确认">
      <div slot="content">
        <div class="confirm-modal">
          <div class="confirm-icon">
            <i class="fas fa-exclamation-triangle text-warning"></i>
          </div>
          <h3>{{ confirmTitle }}</h3>
          <p>{{ confirmText }}</p>
        </div>
      </div>
    </ykc-operation-dialog>
  </scroll-layout>
</template>

<script>
  import { gunWhitelistApi, barrierPage } from '@/service/apis';
  import { code } from '@/utils';
  import AddOrEdit from './AddOrEdit.vue';

  const ENABLE_STATUS = {
    OFF: 0,
    ON: 1,
  };

  export default {
    name: 'GunWhitelistList',
    components: {
      AddOrEdit,
    },
    data() {
      return {
        tableData: [],
        pageInfo: {
          current: 1,
          size: 10,
          total: 0,
        },
        searchParams: {
          stationId: '',
          stationCode: '',
          gunCode: '',
          enableStatus: '',
          effectiveStatus: '',
        },
        stationIdData: [],
        // 抽屉
        showDrawer: false,
        drawerType: 'add', // 'add' | 'edit'
        currentRow: null,
        // 确认弹窗
        confirmVisible: false,
        confirmLoading: false,
        confirmTitle: '',
        confirmText: '',
        confirmType: '', // 'enable' | 'disable' | 'batchEnable' | 'batchDisable'
        confirmRow: null,
        // 批量
        selectedRows: [],
        selectedIds: [],
      };
    },
    computed: {
      searchData() {
        return [
          {
            comName: 'YkcDropdown',
            key: 'stationId',
            label: '电站名称',
            placeholder: '请选择电站',
            data: this.stationIdData,
            filterable: true,
          },
          {
            comName: 'YkcInput',
            key: 'stationCode',
            label: '电站编码',
            placeholder: '请输入电站编码',
          },
          {
            comName: 'YkcInput',
            key: 'gunCode',
            label: '电桩编码',
            placeholder: '请输入电桩编码',
          },
          {
            comName: 'YkcDropdown',
            key: 'enableStatus',
            label: '开启状态',
            placeholder: '请选择开启状态',
            data: [
              { id: 1, name: '已启用' },
              { id: 0, name: '已关闭' },
            ],
          },
          {
            comName: 'YkcDropdown',
            key: 'effectiveStatus',
            label: '生效状态',
            placeholder: '请选择生效状态',
            data: [
              { id: 1, name: '待生效' },
              { id: 2, name: '生效中' },
              { id: 3, name: '已失效' },
            ],
          },
        ];
      },
      drawerTitle() {
        return this.drawerType === 'edit' ? '编辑充电枪白名单' : '添加充电枪白名单';
      },
      drawerButtons() {
        return [
          {
            label: '取消',
            type: 'plain',
            enabled: () => true,
            handler: () => {
              this.onDrawerCancel(() => {
                this.showDrawer = false;
              });
            },
          },
          {
            label: '保存',
            enabled: () => true,
            handler: () => {
              this.$refs.addOrEdit?.submit();
            },
          },
        ];
      },
      tableColumns() {
        return [
          { label: '电桩编码', prop: 'gunCode', minWidth: 140 },
          { label: '电站名称', prop: 'stationName', minWidth: 150 },
          { label: '电站编码', prop: 'stationCode', minWidth: 140 },
          { label: '生效时间', prop: 'effectiveTime', minWidth: 160 },
          { label: '失效时间', prop: 'expireTime', minWidth: 160 },
          {
            label: '开启状态',
            prop: 'enableStatusText',
            minWidth: 90,
            render: (h, data, { row }) => {
              const cls =
                Number(row.enableStatus) === ENABLE_STATUS.ON ? 'status-on' : 'status-off';
              return <span class={cls}>{row.enableStatusText || '-'}</span>;
            },
          },
          {
            label: '生效状态',
            prop: 'effectiveStatusText',
            minWidth: 90,
            render: (h, data, { row }) => {
              return <span>{row.effectiveStatusText || '-'}</span>;
            },
          },
          { label: '备注', prop: 'reason', minWidth: 160 },
          { label: '最后编辑人', prop: 'updateUserName', minWidth: 100 },
          { label: '最后编辑时间', prop: 'updateTime', minWidth: 160 },
        ];
      },
      tableOperateButtons() {
        return [
          {
            enabled: () => code('operation:gunWhitelist:enable'),
            render: (h, data, { row }) => {
              if (Number(row.enableStatus) !== ENABLE_STATUS.OFF) return '';
              return (
                <ykc-button type="text" onClick={() => this.handleEnable(row)}>
                  启用
                </ykc-button>
              );
            },
          },
          {
            enabled: () => code('operation:gunWhitelist:disable'),
            render: (h, data, { row }) => {
              if (Number(row.enableStatus) !== ENABLE_STATUS.ON) return '';
              return (
                <ykc-button type="text" onClick={() => this.handleDisable(row)}>
                  停用
                </ykc-button>
              );
            },
          },
          {
            enabled: () => code('operation:gunWhitelist:edit'),
            render: (h, data, { row }) => {
              return (
                <ykc-button type="text" onClick={() => this.handleEdit(row)}>
                  编辑
                </ykc-button>
              );
            },
          },
        ];
      },
    },
    created() {
      this.getStationData();
    },
    mounted() {
      this.requestList();
    },
    methods: {
      // 获取淘淘充电站下拉数据
      getStationData() {
        barrierPage
          .findStationList({})
          .then(res => {
            this.stationIdData = (res || []).map(item => ({
              id: item.stationId,
              name: item.stationName,
            }));
          })
          .catch(err => {
            console.error('获取电站列表失败:', err);
            this.stationIdData = [];
          });
      },
      requestList() {
        const params = {
          ...this.searchParams,
          current: this.pageInfo.current,
          size: this.pageInfo.size,
        };
        gunWhitelistApi
          .getList(params)
          .then(res => {
            this.tableData = res?.records || [];
            this.pageInfo.total = res?.total || 0;
          })
          .catch(err => {
            console.error('获取列表失败:', err);
          });
      },
      handleSearchConfirm() {
        this.pageInfo.current = 1;
        this.requestList();
      },
      handleSearchClean() {
        Object.assign(this.searchParams, {
          stationId: '',
          stationCode: '',
          gunCode: '',
          enableStatus: '',
          effectiveStatus: '',
        });
        this.pageInfo.current = 1;
        this.requestList();
      },
      handlePaginationSizeChange(size) {
        this.pageInfo.size = size;
        this.pageInfo.current = 1;
        this.requestList();
      },
      handlePaginationCurrentChange(current) {
        this.pageInfo.current = current;
        this.requestList();
      },
      // 添加
      handleAdd() {
        this.drawerType = 'add';
        this.currentRow = null;
        this.showDrawer = true;
      },
      // 编辑
      handleEdit(row) {
        this.drawerType = 'edit';
        this.currentRow = { ...row };
        this.showDrawer = true;
      },
      onDrawerClose(done) {
        done();
      },
      onDrawerCancel(drawerDone) {
        this.$dialog({
          dialogType: 'feedback',
          showTitle: false,
          showFooter: true,
          desc: '取消后操作将不被保存，确认是否取消？',
          onCancel: dialogDone => {
            dialogDone();
          },
          onConfirm: dialogDone => {
            dialogDone();
            drawerDone();
          },
        });
      },
      handleDrawerSuccess() {
        this.showDrawer = false;
        this.requestList();
      },
      // 单条启用
      handleEnable(row) {
        this.confirmType = 'enable';
        this.confirmRow = row;
        this.confirmTitle = '启用';
        this.confirmText = `确认启用电桩【${row.gunCode}】的白名单配置吗？`;
        this.confirmVisible = true;
      },
      // 单条停用
      handleDisable(row) {
        this.confirmType = 'disable';
        this.confirmRow = row;
        this.confirmTitle = '停用';
        this.confirmText = `确认停用电桩【${row.gunCode}】的白名单配置吗？`;
        this.confirmVisible = true;
      },
      // 批量开启（所选须全部为已关闭）
      handleBatchEnable() {
        const invalid = this.selectedRows.filter(
          row => Number(row.enableStatus) !== ENABLE_STATUS.OFF
        );
        if (invalid.length > 0) {
          this.$message.warning('所选记录须全部处于【已关闭】状态才能批量开启');
          return;
        }
        this.confirmType = 'batchEnable';
        this.confirmTitle = '批量开启';
        this.confirmText = `确认开启选中的${this.selectedIds.length}条白名单配置吗？`;
        this.confirmVisible = true;
      },
      // 批量关闭（所选须全部为已启用）
      handleBatchDisable() {
        const invalid = this.selectedRows.filter(
          row => Number(row.enableStatus) !== ENABLE_STATUS.ON
        );
        if (invalid.length > 0) {
          this.$message.warning('所选记录须全部处于【已启用】状态才能批量关闭');
          return;
        }
        this.confirmType = 'batchDisable';
        this.confirmTitle = '批量关闭';
        this.confirmText = `确认关闭选中的${this.selectedIds.length}条白名单配置吗？`;
        this.confirmVisible = true;
      },
      async handleConfirmSubmit(done) {
        try {
          this.confirmLoading = true;
          if (this.confirmType === 'enable') {
            await gunWhitelistApi.batchEnable({ ids: [this.confirmRow.id] });
            this.$message.success('启用成功');
          } else if (this.confirmType === 'disable') {
            await gunWhitelistApi.batchDisable({ ids: [this.confirmRow.id] });
            this.$message.success('停用成功');
          } else if (this.confirmType === 'batchEnable') {
            await gunWhitelistApi.batchEnable({ ids: this.selectedIds });
            this.$message.success('批量开启成功');
          } else if (this.confirmType === 'batchDisable') {
            await gunWhitelistApi.batchDisable({ ids: this.selectedIds });
            this.$message.success('批量关闭成功');
          }
          this.confirmVisible = false;
          this.requestList();
          done();
        } catch (error) {
          console.error('操作失败:', error);
          this.$message.error(error?.message || '操作失败');
        } finally {
          this.confirmLoading = false;
        }
      },
      onConfirmClose(done) {
        done();
      },
      handleSelectionChange(selection) {
        this.selectedRows = [...selection];
        this.selectedIds = selection.map(item => item.id);
      },
    },
  };
</script>

<style lang="scss" scoped>
  .gun-whitelist-list {
    .status-on {
      padding: 2px 8px;
      border-radius: 4px;
      font-size: 12px;
      background: #d1fae5;
      color: #065f46;
    }

    .status-off {
      padding: 2px 8px;
      border-radius: 4px;
      font-size: 12px;
      background: #fee2e2;
      color: #991b1b;
    }

    .text-warning {
      color: #f59e0b;
    }

    .confirm-modal {
      text-align: center;
      padding: 20px 0;

      .confirm-icon {
        width: 48px;
        height: 48px;
        margin: 0 auto 16px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #fef3c7;

        i {
          font-size: 20px;
        }
      }

      h3 {
        font-size: 16px;
        color: #1e293b;
        margin-bottom: 12px;
      }

      p {
        font-size: 14px;
        color: #64748b;
      }
    }
  }
</style>
